/// <reference types="applepayjs" />
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  DEFAULT_APPLE_PAY_BUTTON_TYPE,
  DEFAULT_APPLE_PAY_CARD_NETWORKS,
  getCanMakeApplePayPayments,
  isSecureApplePayContext,
  loadApplePaySdk,
  toApplePayNetwork,
  getApplePayWindow,
} from './utils';
import type { ApplePayButtonHandle, ApplePayButtonProps, ApplePayCodeRenderMode } from './types';
import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import { getTopLevelHostname } from '../../../utils/location';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    // The <apple-pay-button> custom element registered by the Apple Pay JS SDK.
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface IntrinsicElements {
      'apple-pay-button': DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
        buttonstyle?: string;
        type?: string;
        locale?: string;
      };
    }
  }
}

const APPLE_PAY_VERSION = 3;
const MERCHANT_CAPABILITIES: ApplePayJS.ApplePayMerchantCapability[] = ['supports3DS'];
const BILLING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['postalAddress', 'email'];
const SHIPPING_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['postalAddress', 'name', 'phone', 'email'];
const EMAIL_ONLY_CONTACT_FIELDS: ApplePayJS.ApplePayContactField[] = ['email'];

// Defined by the Apple Pay JS SDK (not in `@types/applepayjs`). In Safari with native Apple
// Pay the SDK installs a stub that has only a no-op `set`.
type ApplePayWebOptions = {
  set: (options: { renderApplePayCodeAs?: ApplePayCodeRenderMode }) => void;
  focusApplePayCodeWindow?: () => void;
};

type MaybeApplePayWindow = { ApplePaySession?: typeof ApplePaySession; ApplePayWebOptions?: ApplePayWebOptions };

/**
 * Apple Pay button built on Apple's Apple Pay JS SDK (mirrors the shared Google
 * Pay button's role). The SDK script is loaded into the button's own document —
 * realm-aware so it works inside an iframe, exactly like the Google Pay `pay.js`
 * loader — and renders the official `<apple-pay-button>` element. The
 * `ApplePaySession` is created synchronously in the click handler (Safari's
 * user-gesture rule); merchant validation and payment processing are delegated to
 * the consumer via callbacks.
 */
export const ApplePayButton = forwardRef<ApplePayButtonHandle, ApplePayButtonProps>(function ApplePayButton(
  {
    amount,
    label,
    countryCode = 'US',
    currencyCode = 'USD',
    creditNetworks,
    requireShipping = false,
    requiredBillingContactFields,
    requiredShippingContactFields,
    buttonType = DEFAULT_APPLE_PAY_BUTTON_TYPE,
    buttonStyle = 'black',
    buttonLocale,
    buttonBorderRadius = 4,
    buttonWidth = '100%',
    buttonHeight = '44px',
    className,
    style,
    isDisabled = false,
    renderApplePayCodeAs,
    onValidateMerchant,
    onPaymentAuthorized,
    onCancel,
    onError,
    onClick,
    onGate,
  }: ApplePayButtonProps,
  ref,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLElement>(null);
  const [isReady, setIsReady] = useState(false);

  // Latest props/callbacks are read through refs so the click handler always sees
  // current values and the mount effect need not re-run.
  const stateRef = useRef({
    amount,
    label,
    countryCode,
    currencyCode,
    creditNetworks,
    requireShipping,
    requiredBillingContactFields,
    requiredShippingContactFields,
    isDisabled,
    renderApplePayCodeAs,
  });
  stateRef.current = {
    amount,
    label,
    countryCode,
    currencyCode,
    creditNetworks,
    requireShipping,
    requiredBillingContactFields,
    requiredShippingContactFields,
    isDisabled,
    renderApplePayCodeAs,
  };
  const callbacksRef = useRef({ onValidateMerchant, onPaymentAuthorized, onCancel, onError, onClick, onGate });
  callbacksRef.current = { onValidateMerchant, onPaymentAuthorized, onCancel, onError, onClick, onGate };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const view = getApplePayWindow(wrapper?.ownerDocument.defaultView ?? window);

    // Load the SDK before probing availability: in non-Safari browsers
    // `ApplePaySession` only exists once the SDK has loaded into this realm.
    if (!view || !isSecureApplePayContext(view)) return undefined;

    // One document does both jobs: it registers the `<apple-pay-button>` custom element
    // (custom elements are per-document) and owns the `ApplePaySession` the click handler
    // constructs. OneClick renders straight into the hosted PayPage document, so that is
    // the top-level page — whose URL is what Apple judges.
    const doc = view.document;
    const status = { disposed: false };

    void loadApplePaySdk(doc)
      .then(() => {
        if (!status.disposed && getCanMakeApplePayPayments(view)) setIsReady(true);
      })
      .catch((error: unknown) => {
        if (!status.disposed) callbacksRef.current.onError?.(error as Error);
      });

    return () => {
      status.disposed = true;
    };
  }, []);

  // Create and begin the Apple Pay session. MUST be called synchronously inside a
  // user-gesture handler (Safari rule) — either this button's own click, or a later
  // gesture (e.g. a fee-modal confirm) when gating via `onGate` + this ref.
  const beginSession = useCallback(() => {
    const props = stateRef.current;
    const callbacks = callbacksRef.current;

    // The session belongs to the button's own realm. Constructed synchronously here so it
    // stays inside the user gesture (Safari's rule).
    const view = getApplePayWindow(wrapperRef.current?.ownerDocument.defaultView ?? window);
    const Session = (view as unknown as MaybeApplePayWindow).ApplePaySession;
    if (!Session) {
      callbacks.onError?.(new Error('Apple Pay is not available'));
      return;
    }

    const supportedNetworks = props.creditNetworks?.length ? props.creditNetworks.map(toApplePayNetwork) : DEFAULT_APPLE_PAY_CARD_NETWORKS;

    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: props.countryCode,
      currencyCode: props.currencyCode,
      total: { label: props.label ?? '', amount: String(props.amount ?? '0') },
      supportedNetworks,
      merchantCapabilities: MERCHANT_CAPABILITIES,
      // TODO - add to shared: the `requiredBillingContactFields` / `requiredShippingContactFields` overrides (PPM-802)
      requiredBillingContactFields: props.requiredBillingContactFields ?? BILLING_CONTACT_FIELDS,
      requiredShippingContactFields:
        props.requiredShippingContactFields ?? (props.requireShipping ? SHIPPING_CONTACT_FIELDS : EMAIL_ONLY_CONTACT_FIELDS),
    };

    let session: ApplePaySession;
    try {
      session = new Session(APPLE_PAY_VERSION, paymentRequest);
    } catch (error) {
      callbacks.onError?.(error as Error);
      return;
    }

    session.onvalidatemerchant = (validateEvent) => {
      // The document creating the session is the domain Apple has a registration for —
      // not `top`, which is the integrator's site when /pay is embedded.
      const domain = getTopLevelHostname(); // TODO: add to shared: `getTopLevelHostname()`
      void callbacksRef.current
        .onValidateMerchant(validateEvent.validationURL, domain)
        .then((merchantSession) => {
          session.completeMerchantValidation(merchantSession);
        })
        .catch((error: unknown) => {
          session.abort();
          callbacksRef.current.onError?.(error as Error);
        });
    };

    session.onpaymentauthorized = (authEvent) => {
      void callbacksRef.current
        .onPaymentAuthorized(authEvent.payment)
        .then((result) => {
          session.completePayment(result.status === 'success' ? Session.STATUS_SUCCESS : Session.STATUS_FAILURE);
          result.onCompleted?.();
        })
        .catch((error: unknown) => {
          session.completePayment(Session.STATUS_FAILURE);
          callbacksRef.current.onError?.(error as Error);
        });
    };

    session.oncancel = () => {
      callbacksRef.current.onCancel?.();
    };

    try {
      // Browsers without native Apple Pay show the code where the SDK reads this setting
      // in `begin()`: a modal over this (possibly tiny iframe) document, or its own window.
      if (props.renderApplePayCodeAs) {
        (view as unknown as MaybeApplePayWindow).ApplePayWebOptions?.set({ renderApplePayCodeAs: props.renderApplePayCodeAs });
      }
      session.begin();
    } catch (error) {
      callbacks.onError?.(error as Error);
    }
  }, []);

  useImperativeHandle(ref, () => ({ beginSession }), [beginSession]);

  const handleClick = useCallback(() => {
    const props = stateRef.current;
    const callbacks = callbacksRef.current;
    if (props.isDisabled) {
      // The code window of the running session can fall behind the page while the button is
      // disabled for that session; a click brings it back (a no-op when no window is open).
      if (props.renderApplePayCodeAs === 'window') {
        const view = buttonRef.current?.ownerDocument.defaultView ?? window;
        (view as unknown as MaybeApplePayWindow).ApplePayWebOptions?.focusApplePayCodeWindow?.();
      }
      return;
    }
    callbacks.onClick?.();

    // When gated, hand control to the consumer instead of opening the sheet now.
    // The consumer runs its confirmation UI and calls `beginSession()` from the
    // confirm gesture. Otherwise, begin immediately in this click.
    if (callbacks.onGate) {
      callbacks.onGate();
      return;
    }
    beginSession();
  }, [beginSession]);

  // Bind the click natively on the custom element once it is rendered; React's
  // delegated onClick does not fire for this shadow-DOM element.
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return undefined;
    button.addEventListener('click', handleClick);
    return () => {
      button.removeEventListener('click', handleClick);
    };
  }, [isReady, handleClick]);

  // The wrapper must stay mounted while probing: its `ownerDocument` is how the effect
  // above finds the document to load the SDK into. Hidden so it takes no layout while the
  // device availability is still unknown (or the device can't pay at all).
  if (!isReady) {
    return <div ref={wrapperRef} className={className} style={{ ...style, display: 'none' }} />;
  }

  return (
    <div ref={wrapperRef} className={classNames(className, { disabled: isDisabled, 'code-window': renderApplePayCodeAs === 'window' })} style={style}>
      {/* Apple's SDK element rather than WebKit's `-apple-pay-button` appearance, which only
          renders in Safari. Requires the element to live in the document the SDK loaded into. */}
      <apple-pay-button
        ref={buttonRef}
        buttonstyle={buttonStyle}
        type={buttonType}
        locale={buttonLocale ?? 'en-US'}
        className='apple-pay-btn'
        style={
          {
            '--apple-pay-button-width': buttonWidth,
            '--apple-pay-button-height': buttonHeight,
            '--apple-pay-button-border-radius': `${String(buttonBorderRadius)}px`,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
